import {
  Button,
  Table,
  TableColumn,
  TableParams,
  TableRowKey,
} from "@datapay-by-datacom/design-system";
import { useMemo, useState, useCallback } from "react";
import {
  OrgAccessRequestOrgCategory,
  OrgAccessRequestRole,
  OrgUserSummary,
  UserOrgRoleSummary,
} from "../../../generated/core-api";
import {
  useGrantOrgAccess,
  useListUsersInMyOrgs,
  useRevokeOrgAccess,
  useUserManagementPermissions,
} from "../../hooks";
import { OrgAccessModal } from "../OrgAccessModal";
import { UserDetailPanel } from "../UserDetailPanel";
import s from "./UserTable.module.css";

export type UserTableData = OrgUserSummary;

function OrgBadge({
  orgRole,
  canEdit,
  onRevoke,
  isRevoking,
}: {
  orgRole: UserOrgRoleSummary;
  canEdit: boolean;
  onRevoke: () => void;
  isRevoking: boolean;
}) {
  return (
    <span
      className={`${s.orgBadge} ${orgRole.role === "admin" ? s.orgBadgeAdmin : ""}`}
      title={`${orgRole.org_category} / ${orgRole.org_id}`}
    >
      {orgRole.org_name || orgRole.org_id}
      <span className={s.roleSuffix}>{orgRole.role}</span>
      {canEdit && (
        <button
          className={s.revokeButton}
          onClick={onRevoke}
          disabled={isRevoking}
          title="Revoke access"
          aria-label={`Revoke ${orgRole.org_name || orgRole.org_id} access`}
        >
          ×
        </button>
      )}
    </span>
  );
}

const createColumns = (
  canEdit: boolean,
  onRevoke: (user: OrgUserSummary, orgRole: UserOrgRoleSummary) => void,
  onGrant: (user: OrgUserSummary) => void,
  onViewDetails: (user: OrgUserSummary) => void,
  revokingKeys: Set<string>,
): TableColumn<UserTableData>[] => {
  const columns: TableColumn<UserTableData>[] = [
    {
      key: "name",
      header: () => "Name",
      cell: (row) => row.name || "-",
    },
    {
      key: "email",
      header: () => "Email",
      cell: (row) => row.email || "-",
    },
    {
      key: "orgMemberships",
      header: () => "Org Memberships",
      cell: (row) => (
        <div className={s.orgRoles}>
          {row.org_roles.map((orgRole) => (
            <OrgBadge
              key={`${orgRole.org_id}-${orgRole.role}`}
              orgRole={orgRole}
              canEdit={canEdit}
              onRevoke={() => onRevoke(row, orgRole)}
              isRevoking={revokingKeys.has(
                `${row.user_id}-${orgRole.org_id}-${orgRole.role}`,
              )}
            />
          ))}
          {row.org_roles.length === 0 && (
            <span className={s.noOrgs}>No org memberships</span>
          )}
        </div>
      ),
    },
  ];

  columns.push({
    key: "actions" as keyof UserTableData,
    header: () => "Actions",
    cell: (row) => (
      <div className={s.actions}>
        <Button variant="secondary" onPress={() => onViewDetails(row)}>
          View
        </Button>
        {canEdit && (
          <Button variant="secondary" onPress={() => onGrant(row)}>
            Grant Access
          </Button>
        )}
      </div>
    ),
  });

  return columns;
};

const rowKey: TableRowKey<UserTableData> = (row) => row.user_id;

export function UserTable() {
  const [params, setParams] = useState<TableParams<UserTableData>>({});
  const usersQuery = useListUsersInMyOrgs();
  const { canEdit } = useUserManagementPermissions();
  const grantMutation = useGrantOrgAccess();
  const revokeMutation = useRevokeOrgAccess();

  const [grantModalUser, setGrantModalUser] = useState<OrgUserSummary | null>(
    null,
  );
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [revokingKeys, setRevokingKeys] = useState<Set<string>>(new Set());

  const selectedUser = selectedUserId
    ? (usersQuery.data as OrgUserSummary[] | undefined)?.find(
        (u) => u.user_id === selectedUserId,
      ) ?? null
    : null;

  const handleRevoke = useCallback((user: OrgUserSummary, orgRole: UserOrgRoleSummary) => {
    const key = `${user.user_id}-${orgRole.org_id}-${orgRole.role}`;
    setRevokingKeys((prev) => {
      if (prev.has(key)) return prev;
      return new Set(prev).add(key);
    });
    revokeMutation.mutate(
      {
        user_id: user.user_id,
        org_id: orgRole.org_id,
        org_category: orgRole.org_category as OrgAccessRequestOrgCategory,
        role: orgRole.role as OrgAccessRequestRole,
      },
      {
        onSettled: () =>
          setRevokingKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          }),
      },
    );
  }, [revokeMutation]);

  const handleGrant = (
    orgId: string,
    orgCategory: string,
    role: OrgAccessRequestRole,
  ) => {
    if (!grantModalUser) return;
    grantMutation.mutate(
      {
        user_id: grantModalUser.user_id,
        org_id: orgId,
        org_category: orgCategory as OrgAccessRequestOrgCategory,
        role,
      },
      {
        onSuccess: () => setGrantModalUser(null),
      },
    );
  };

  const handleDetailRevoke = (orgRole: UserOrgRoleSummary) => {
    if (!selectedUser) return;
    handleRevoke(selectedUser, orgRole);
  };

  const handleDetailGrantAccess = () => {
    if (!selectedUser) return;
    setGrantModalUser(selectedUser);
  };

  const columns = useMemo(
    () => createColumns(
      canEdit,
      handleRevoke,
      setGrantModalUser,
      (user: OrgUserSummary) => setSelectedUserId(user.user_id),
      revokingKeys,
    ),
    [canEdit, handleRevoke, revokingKeys],
  );

  return (
    <div className={s.container}>
      <Table
        description="Users"
        config={{ columns, rowKey }}
        control={{ params, onParamsChange: setParams }}
        query={usersQuery}
      />
      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onClose={() => setSelectedUserId(null)}
          onGrantAccess={handleDetailGrantAccess}
          onRevoke={handleDetailRevoke}
          revokingKeys={revokingKeys}
        />
      )}
      {grantModalUser && (
        <OrgAccessModal
          user={grantModalUser}
          isOpen={true}
          onClose={() => setGrantModalUser(null)}
          onGrant={handleGrant}
          isPending={grantMutation.isPending}
        />
      )}
    </div>
  );
}
